import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import styles from './grid.less';
import defaultLocaleSorter from 'utils/locale-aware-sorter';

const GRID_THEMES = ['light', 'dark'];

/**
 * A Factory function which returns a decorated Grid component whose styles are scoped
 * to the CSS modules styles of this component
 *
 * @param {React.component} GridComponent - the Grid component (eg: AgGridReact)
 * @returns {Function} DecoratedComponent - a stateless function which wraps the GridComponent applying
 *                                          the appropriate classNames.
 **/
const gridFactory = GridComponent => {
  class Grid extends PureComponent {
    static propTypes = {
      ...GridComponent.propTypes,
      className: PropTypes.string,
      height: PropTypes.number,
      padding: PropTypes.object,
      onGridReady: PropTypes.func,
      reactNext: PropTypes.bool,
      style: PropTypes.object,
      theme: PropTypes.oneOf(GRID_THEMES),
      width: PropTypes.number,
      withViewportBorder: PropTypes.bool,
    };

    static defaultProps = {
      ...GridComponent.defaultProps,
      autoSizeColumns: true,
      className: '',
      defaultColDef: {
        comparator: defaultLocaleSorter.compare,
      },
      icons: {
        sortAscending: '<i class="fa fa-sort-down" />',
        sortDescending: '<i class="fa fa-sort-up" />',
      },
      onGridReady: () => {},
      padding: {},

      // Use React 16 features (such as portals) by default
      // @see https://www.ag-grid.com/react-more-details/#react-portals
      reactNext: true,

      rowHeight: 43,
      suppressCellSelection: false,
      theme: 'light',
      withViewportBorder: false,
    };

    constructor(props) {
      super(props);
      this.onGridReady = this.onGridReady.bind(this);
    }

    /**
     * Callback that is fired when ag-grid has initialised.
     * Store the api and columnApi on this instance for later use.
     *
     * @param {Object} params - parameter object for AgGrid
     **/
    onGridReady({ api, columnApi }) {
      this.api = api;
      this.columnApi = columnApi;

      // Call the onGridReady passed in via props
      this.props.onGridReady({ api, columnApi });
    }

    render() {
      const {
        className,
        height,
        padding,
        style,
        theme,
        width,
        withViewportBorder,
        onGridReady, // eslint-disable-line no-unused-vars
        ...otherProps
      } = this.props;

      const _className = classnames(styles.root, className);

      const _themeClassName = classnames('ag-theme-balham', styles.theme, {
        [styles['with-viewport-border']]: withViewportBorder,
        [styles[`theme--${theme}`]]: theme,
      });

      // Abstract the padding prop and re-assign to camel case variants that are compatible with
      // React inline styles.
      const {
        top: paddingTop,
        right: paddingRight,
        bottom: paddingBottom,
        left: paddingLeft,
      } = padding;

      // Combine all of the style props into a final combined style.
      const _style = {
        ...style,
        width,
        height,
        paddingTop,
        paddingRight,
        paddingBottom,
        paddingLeft,
      };

      return (
        <div className={_className} style={_style}>
          <div className={_themeClassName}>
            <GridComponent onGridReady={this.onGridReady} {...otherProps} />
          </div>
        </div>
      );
    }
  }

  return Grid;
};

export default gridFactory;
export { gridFactory };
